import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Modal } from '@/components';
import {
	CREATE_GROUP_CANCEL_LABEL,
	CREATE_GROUP_STUDENTS_EMPTY_TEXT,
	CREATE_GROUP_STUDENTS_LABEL,
	CREATE_GROUP_STUDENTS_LOADING_TEXT,
	CREATE_GROUP_SUBMIT_LABEL,
	GROUP_NAME_MAX_LENGTH,
	GROUP_NAME_PLACEHOLDER,
	STUDENT_AVATAR_TONE_COUNT
} from '../../boutique-classroom.constants';
import StudentPickerList from '../create-event-modal/StudentPickerList';
import { CreateGroupFormValues, StudentPickerItem } from '../../boutique-classroom.types';
import '@/assets/styles/views/boutique-classroom/create-group-modal.styles.scss';

interface CreateGroupModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: CreateGroupFormValues) => void;
	students?: StudentPickerItem[];
	availableStudents?: StudentPickerItem[];
	isLoadingStudents?: boolean;
	isSubmitting?: boolean;
}

const EMPTY_STUDENTS: StudentPickerItem[] = [];
const GROUP_NAME_INPUT_ID = 'cgm-group-name';

function getInitials(fullName: string): string {
	return fullName
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part.charAt(0).toLocaleUpperCase('tr-TR'))
		.join('');
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ isOpen, onClose, onSubmit, students = EMPTY_STUDENTS, availableStudents, isLoadingStudents = false, isSubmitting = false }) => {
	const [name, setName] = useState('');
	const [removedIds, setRemovedIds] = useState<number[]>([]);
	const [pickedIds, setPickedIds] = useState<number[]>([]);
	const isPickerMode = availableStudents !== undefined;

	useEffect(() => {
		if (isOpen) {
			setName('');
			setRemovedIds([]);
			setPickedIds(students.map(student => student.id));
		}
	}, [isOpen, students]);

	const pickerStudents = useMemo<StudentPickerItem[]>(() => {
		if (!isPickerMode) return EMPTY_STUDENTS;
		const seen = new Set<number>();
		return [...students, ...(availableStudents ?? [])].filter(student => (seen.has(student.id) ? false : (seen.add(student.id), true)));
	}, [availableStudents, isPickerMode, students]);

	const visibleStudents = useMemo(
		() => (isPickerMode ? pickerStudents.filter(student => pickedIds.includes(student.id)) : students.filter(student => !removedIds.includes(student.id))),
		[isPickerMode, pickedIds, pickerStudents, removedIds, students]
	);
	const trimmedName = name.trim();
	const canSubmit = trimmedName.length > 0 && !isSubmitting && (!isPickerMode || visibleStudents.length > 0);

	const handleTogglePicked = useCallback((studentId: number) => {
		setPickedIds(prev => (prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]));
	}, []);

	const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setName(event.target.value.slice(0, GROUP_NAME_MAX_LENGTH));
	}, []);

	const handleRemove = useCallback((studentId: number) => {
		setRemovedIds(prev => [...prev, studentId]);
	}, []);

	const handleSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			if (!canSubmit) return;
			onSubmit({ name: trimmedName, studentIds: visibleStudents.map(student => student.id) });
		},
		[canSubmit, onSubmit, trimmedName, visibleStudents]
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			hasHeader
			title="Yeni Grup Oluştur"
			showCloseButton
			size="md"
			variant="centered"
			closeOnClickOutside={false}
			className="cgm-modal"
			contentClassName="cgm-modal__content"
			headerClassName="cgm-modal__header"
			bodyClassName="cgm-modal__body">
			<form
				className="cgm"
				onSubmit={handleSubmit}
				noValidate>
				<div className="cgm__scroll">
					<p className="cgm__description">Seçtiğiniz öğrencileri aynı grupta toplayın. Grup adı daha sonra da değiştirilebilir.</p>

					<div className="cgm-field">
						<label
							className="cgm-field__label"
							htmlFor={GROUP_NAME_INPUT_ID}>
							Grup Adı
						</label>
						<div className="cgm-input">
							<input
								id={GROUP_NAME_INPUT_ID}
								type="text"
								className="cgm-input__control"
								value={name}
								onChange={handleNameChange}
								placeholder={GROUP_NAME_PLACEHOLDER}
								maxLength={GROUP_NAME_MAX_LENGTH}
								autoComplete="off"
							/>
							<span className="cgm-input__counter">
								{name.length}/{GROUP_NAME_MAX_LENGTH}
							</span>
						</div>
					</div>

					{isPickerMode ? (
						<div className="cgm-field">
							<span className="cgm-field__label">
								{CREATE_GROUP_STUDENTS_LABEL} ({visibleStudents.length})
							</span>
							{isLoadingStudents && pickerStudents.length === 0 ? (
								<div className="cgm-students cgm-students--empty">{CREATE_GROUP_STUDENTS_LOADING_TEXT}</div>
							) : (
								<StudentPickerList
									students={pickerStudents}
									selectedIds={pickedIds}
									onToggle={handleTogglePicked}
									emptyText={CREATE_GROUP_STUDENTS_EMPTY_TEXT}
								/>
							)}
						</div>
					) : (
						<div className="cgm-field">
							<span className="cgm-field__label">Seçilen Öğrenciler ({visibleStudents.length})</span>
							{visibleStudents.length > 0 ? (
								<ul className="cgm-students">
									{visibleStudents.map(student => (
										<li
											key={student.id}
											className="cgm-student">
											<span className={clsx('cgm-student__avatar', `cgm-student__avatar--tone-${student.id % STUDENT_AVATAR_TONE_COUNT}`)}>{getInitials(student.fullName)}</span>
											<span className="cgm-student__text">
												<span className="cgm-student__name">{student.fullName}</span>
												<span className="cgm-student__meta">{student.schoolName}</span>
											</span>
											<button
												type="button"
												className="cgm-student__remove"
												aria-label={`${student.fullName} öğrencisini listeden çıkar`}
												onClick={() => handleRemove(student.id)}>
												<i className="icons-minus-circle" />
											</button>
										</li>
									))}
								</ul>
							) : (
								<div className="cgm-students cgm-students--empty">Bu gruba eklenecek öğrenci seçilmedi.</div>
							)}
						</div>
					)}

					{visibleStudents.length > 0 && (
						<div className="cgm-info">
							<i className="icons-info-circle" />
							<span>Bu öğrenciler mevcut gruplarından çıkartılarak {trimmedName ? `"${trimmedName}" grubuna` : 'yeni gruba'} alınacaktır.</span>
						</div>
					)}
				</div>

				<div className="cgm__footer">
					<button
						type="button"
						className="cgm-button cgm-button--secondary"
						onClick={onClose}
						disabled={isSubmitting}>
						{CREATE_GROUP_CANCEL_LABEL}
					</button>
					<button
						type="submit"
						className="cgm-button cgm-button--primary"
						disabled={!canSubmit}>
						{CREATE_GROUP_SUBMIT_LABEL}
					</button>
				</div>
			</form>
		</Modal>
	);
};

export default CreateGroupModal;
